import React, { ReactNode, useState } from "react";
import "./PostCarousel.css";
import { addHttpToLink } from "../../../Utils/StayUpToDate/RenderComponent";
import { useDesign } from "../../../context/DesignContext";
import { CaptionFlatBackgroundContext } from "./CaptionSurfaceContext";
import { isInsideWebsiteTemplatePage } from "../../../Utils/websiteTemplateGuards";

export default function Caption(props: any) {
  const { caption } = props;
  const [showAll, setShowAll] = useState(false);
  const maxCharLength = 150;
  const { backgroundStyle, templateName } = useDesign();
  const captionFlatBackground = React.useContext(CaptionFlatBackgroundContext);

  const isDefaultTemplate =
    (templateName || "").toLowerCase() === "default" || !templateName;

  // Set CSS custom properties for template colors (skip when captions are flat on canvas)
  React.useEffect(() => {
    if (captionFlatBackground || isInsideWebsiteTemplatePage()) return;
    document.documentElement.style.setProperty("--template-background", backgroundStyle);
  }, [backgroundStyle, captionFlatBackground]);

  // Function to convert URLs and phone numbers to clickable links
  const convertToClickable = (text: string) => {
    // More specific URL pattern to avoid double matches
    // const urlPattern = /(?:https?:\/\/)?[\w-]+(?:\.[\w-]+)+(?:[\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?/g;
    // Updated URL regex – matches only proper URLs (not numbers like 1.0)
    // const urlPattern = /\b(?:(?:https?:\/\/|www\.)[^\s<>]+|[a-zA-Z0-9-]+\.(?:com|org|net|io|co|in|edu|gov)(?:\/[^\s<>]*)?)\b/g;

    const urlPattern = /\b(?:(?:https?:\/\/|www\.)[^\s]+|[\w-]+\.(?:com|org|net|in|co|io|gov|edu|info|me|us|uk)(?:\/[^\s]*)?)\b/gi;
    const phonePattern = /\b\d{10}\b/;
    const processedPhoneNumbers = new Set<string>();
    const processedUrls = new Set<string>();

    // First find all matches
    const parts = [];
    let lastIndex = 0;
    
    // Process URLs
    text.replace(urlPattern, (match, offset) => {
      if (!processedUrls.has(match)) {
        if (offset > lastIndex) {
          parts.push(text.slice(lastIndex, offset));
        }
        parts.push(match);
        processedUrls.add(match);
        lastIndex = offset + match.length;
      }
      return match;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.map((part, index) => {
      if (processedUrls.has(part)) {
        return (
          <a 
            key={index} 
            href={addHttpToLink(part)}
            target="_blank" 
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }

      if (phonePattern.test(part)) {
        const phoneMatcher = new RegExp(phonePattern.source, "g");
        const phoneSegments: ReactNode[] = [];
        let hasPhoneMatch = false;
        let lastPhoneIndex = 0;
        let phoneCounter = 0;
        let phoneMatch: RegExpExecArray | null = phoneMatcher.exec(part);

        while (phoneMatch) {
          hasPhoneMatch = true;
          const matchText = phoneMatch[0];
          const matchIndex = phoneMatch.index ?? 0;

          if (matchIndex > lastPhoneIndex) {
            phoneSegments.push(part.slice(lastPhoneIndex, matchIndex));
          }

          if (!processedPhoneNumbers.has(matchText)) {
            processedPhoneNumbers.add(matchText);
            phoneSegments.push(
              <a
                key={`phone-${index}-${phoneCounter}`}
                href={`tel:${matchText}`}
                onClick={(e) => e.stopPropagation()}
              >
                {matchText}
              </a>
            );
          } else {
            phoneSegments.push(matchText);
          }

          lastPhoneIndex = matchIndex + matchText.length;
          phoneCounter += 1;
          phoneMatch = phoneMatcher.exec(part);
        }

        if (hasPhoneMatch) {
          if (lastPhoneIndex < part.length) {
            phoneSegments.push(part.slice(lastPhoneIndex));
          }

          return (
            <span key={`phone-wrapper-${index}`}>{phoneSegments}</span>
          );
        }
      }

      return part;
    });
  };

  return (
    <>
      {caption && caption.length > 0 && (
        <div
          className="posts-caption"
          id="post-caption"
          style={{
            background:
              captionFlatBackground || !caption.trim().length
                ? "transparent"
                : "var(--template-background, #fcfcfc)",
            ...("designTokens" in props &&
              props?.designTokens?.text_color && {
                fontFamily: props?.designTokens?.secondaryFontFamily,
                color: props.designTokens.text_color,
              }),
          }}
        >
          {showAll || caption.length <= maxCharLength ? (
            convertToClickable(caption)
          ) : (
            <><p style={{marginBottom:0}}>
              {convertToClickable(caption.substring(0, maxCharLength))}
            
              <span
                className={
                  isDefaultTemplate
                    ? "posts-caption-more"
                    : "posts-caption-more posts-caption-more--accent"
                }
                onClick={() => {
                  setShowAll(true);
                }}
              >
                {!props.isNewsletter ? (
                  <>... see more</>
                ) : (
                  <button
                    className={
                      isDefaultTemplate
                        ? "posts-caption-more-btn"
                        : "posts-caption-more-btn posts-caption-more-btn--accent"
                    }
                    onClick={() => props.setIsModalOpen(true)}
                  >
                    ... see more
                  </button>
                )}
              </span>
              </p>
            </>
          )}
        </div>
      )}
    </>
  );
}
